import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Twitter, Mail } from "lucide-react";
import { LogoMark } from "./Logo";
import { useSite } from "@/lib/SiteContext";

const SOCIALS = [
  { key: "instagram", label: "Instagram", Icon: Instagram, tid: "social-instagram-link" },
  { key: "facebook", label: "Facebook", Icon: Facebook, tid: "social-facebook-link" },
  { key: "youtube", label: "YouTube", Icon: Youtube, tid: "social-youtube-link" },
  { key: "x", label: "X (Twitter)", Icon: Twitter, tid: "social-x-link" },
];

export default function Footer() {
  const { settings, flags } = useSite();
  const socials = settings.socials || {};
  const year = new Date().getFullYear();

  return (
    <footer className="bg-ink border-t border-paper/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_primary.png`}
            alt="Dirty Church Chronicles — official logo"
            className="w-56 sm:w-64 h-auto"
            data-testid="footer-logo"
          />
          <p className="font-ui text-[10px] tracking-[0.35em] text-crimson uppercase mt-4">
            Truth that confronts. A call to return.
          </p>
          <p className="font-reading text-paper/60 text-lg leading-relaxed mt-6 max-w-md">
            A Christian nonfiction series by {settings.author_name || "Jermaine Miller"}.
          </p>
          {Object.values(socials).some(Boolean) && (
            <div className="flex gap-4 mt-6">
              {SOCIALS.filter((s) => socials[s.key]).map(({ key, label, Icon, tid }) => (
                <a
                  key={key}
                  href={socials[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={tid}
                  className="text-paper/50 hover:text-crimsonhi transition-colors duration-300"
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-3">
          <p className="eyebrow mb-5">Explore</p>
          <ul className="space-y-3 font-ui text-sm text-paper/60">
            <li><Link className="hover:text-paper transition-colors duration-300" to="/book" data-testid="footer-book-link">The Book</Link></li>
            <li><Link className="hover:text-paper transition-colors duration-300" to="/bookstore" data-testid="footer-bookstore-link">Official Bookstore</Link></li>
            <li><Link className="hover:text-paper transition-colors duration-300" to="/merch" data-testid="footer-merch-link">Official Merch</Link></li>
            <li><Link className="hover:text-paper transition-colors duration-300" to="/cart" data-testid="footer-cart-link">Shopping Cart</Link></li>
            <li><Link className="hover:text-paper transition-colors duration-300" to="/author" data-testid="footer-author-link">The Author</Link></li>
            {flags.excerpts > 0 && (
              <li><Link className="hover:text-paper transition-colors duration-300" to="/chronicles" data-testid="footer-chronicles-link">Inside the Chronicles</Link></li>
            )}
            {flags.media > 0 && (
              <li><Link className="hover:text-paper transition-colors duration-300" to="/cinema" data-testid="footer-cinema-link">Cinema</Link></li>
            )}
            <li><Link className="hover:text-paper transition-colors duration-300" to="/appearances" data-testid="footer-appearances-link">Appearances</Link></li>
            <li><Link className="hover:text-paper transition-colors duration-300" to="/connect" data-testid="footer-connect-link">Connect</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="eyebrow mb-5">Contact</p>
          {settings.contact_email ? (
            <a
              href={`mailto:${settings.contact_email}`}
              data-testid="footer-contact-email"
              className="inline-flex items-center gap-2 font-ui text-sm text-paper/60 hover:text-paper transition-colors duration-300"
            >
              <Mail size={16} /> {settings.contact_email}
            </a>
          ) : (
            <p className="font-ui text-sm text-paper/60">
              For speaking, media, and bulk copies, use the{" "}
              <Link to="/connect" className="text-crimsonhi underline underline-offset-4">inquiry form</Link>.
            </p>
          )}
          <p className="font-ui text-xs text-paper/40 leading-relaxed mt-6 max-w-sm">
            Privacy: we collect only the information you choose to submit through the reader list and inquiry
            form, with your explicit consent. It is never sold or shared.
          </p>
        </div>
      </div>
      <div className="border-t border-paper/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-ui text-xs text-paper/40" data-testid="footer-copyright">
            © {year} Dirty Church Chronicles. All rights reserved.
          </p>
          <Link
            to="/admin"
            data-testid="footer-owner-login-link"
            className="font-ui text-[10px] uppercase tracking-[0.3em] text-paper/30 hover:text-paper/70 transition-colors duration-300"
          >
            Owner
          </Link>
        </div>
      </div>
    </footer>
  );
}
